import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { FaCarAlt } from 'react-icons/fa';
import { getBrands, getModelsByBrand } from '../services/vehicleDataService';

const VehicleForm = ({ isActive, formData, handleChange, handlePrev, handleNext }) => {
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);

    // Component aktif olduğunda markaları yükle
    useEffect(() => {
        if (isActive) {
            loadBrands();
        }
    }, [isActive]);

    // Marka değiştiğinde modelleri yükle
    useEffect(() => {
        if (formData.preferredVehicleBrand) {
            loadModels(formData.preferredVehicleBrand);
        } else {
            setModels([]);
            // Model seçimini temizle
            if (formData.preferredVehicleModel) {
                handleChange({
                    target: {
                        name: 'preferredVehicleModel',
                        value: ''
                    }
                });
            }
        }
    }, [formData.preferredVehicleBrand]);

    const loadBrands = async () => {
        try {
            setLoadingBrands(true);
            console.log('🚗 Loading brands for application...');

            const response = await getBrands();

            if (response.success) {
                setBrands(response.data || []);
                console.log(`✅ Loaded ${response.data?.length} brands`);
            } else {
                console.error('❌ Failed to load brands:', response.message);
                setBrands([]);
            }
        } catch (error) {
            console.error('❌ Error loading brands:', error);
            setBrands([]);
        } finally {
            setLoadingBrands(false);
        }
    };

    const loadModels = async (brandName) => {
        try {
            setLoadingModels(true);
            console.log(`🚗 Loading models for brand: ${brandName}`);

            const response = await getModelsByBrand(brandName);

            if (response.success) {
                setModels(response.data || []);
                console.log(`✅ Loaded ${response.data?.length} models for ${brandName}`);
            } else {
                console.error('❌ Failed to load models:', response.message);
                setModels([]);
            }
        } catch (error) {
            console.error('❌ Error loading models:', error);
            setModels([]);
        } finally {
            setLoadingModels(false);
        }
    };

    // Yıl dropdown'u için array oluştur
    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear; year >= currentYear - 25; year--) {
            years.push(year);
        }
        return years;
    };

    return (
        <div id="arac-form" className={isActive ? 'd-block' : 'd-none'}>
            <h4 className="form-section-title">
                <FaCarAlt className="me-2" /> Araç Bilgileri
            </h4>

            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Tercih Ettiğiniz Araç Markası</Form.Label>
                        <Form.Select
                            name="preferredVehicleBrand"
                            value={formData.preferredVehicleBrand}
                            onChange={handleChange}
                            disabled={loadingBrands}
                        >
                            <option value="">
                                {loadingBrands ? 'Markalar yükleniyor...' : 'Marka seçiniz'}
                            </option>
                            {brands.map(brand => (
                                <option key={brand} value={brand}>
                                    {brand}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            {brands.length > 0 ? `${brands.length} marka mevcut` : 'Listede bulunmayan markalar için destek ekibimizle iletişime geçebilirsiniz'}
                        </Form.Text>
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Tercih Ettiğiniz Araç Modeli</Form.Label>
                        <Form.Select
                            name="preferredVehicleModel"
                            value={formData.preferredVehicleModel}
                            onChange={handleChange}
                            disabled={!formData.preferredVehicleBrand || loadingModels}
                        >
                            <option value="">
                                {!formData.preferredVehicleBrand ? 'Önce marka seçiniz' :
                                    loadingModels ? 'Modeller yükleniyor...' :
                                        'Model seçiniz'}
                            </option>
                            {models.map(model => (
                                <option key={model.name} value={model.name}>
                                    {model.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            {formData.preferredVehicleBrand && models.length === 0 && !loadingModels &&
                                'Bu marka için model bulunamadı'}
                        </Form.Text>
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Tercih Ettiğiniz Model Yılı</Form.Label>
                        <Form.Select
                            name="vehicleYear"
                            value={formData.vehicleYear}
                            onChange={handleChange}
                        >
                            <option value="">Yıl seçiniz</option>
                            {getYearOptions().map(year => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Araç model yılını seçiniz
                        </Form.Text>
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Peşinat Miktarı (₺)</Form.Label>
                        <Form.Control
                            type="number"
                            name="downPayment"
                            value={formData.downPayment}
                            onChange={handleChange}
                            placeholder="Ödeyebileceğiniz peşinat miktarı"
                            min="0"
                            step="1000"
                        />
                        <Form.Text className="text-muted">
                            Varsa peşinat tutarınızı giriniz (isteğe bağlı)
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            {/* Seçilen Araç Özeti */}
            {(formData.preferredVehicleBrand || formData.preferredVehicleModel || formData.vehicleYear) && (
                <div className="mb-4 p-3 bg-light rounded">
                    <h6 className="mb-2">
                        <FaCarAlt className="me-2" />
                        Seçilen Araç Bilgileri
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                        {formData.preferredVehicleBrand && (
                            <span className="badge bg-primary">
                                Marka: {formData.preferredVehicleBrand}
                            </span>
                        )}
                        {formData.preferredVehicleModel && (
                            <span className="badge bg-success">
                                Model: {formData.preferredVehicleModel}
                            </span>
                        )}
                        {formData.vehicleYear && (
                            <span className="badge bg-info">
                                Yıl: {formData.vehicleYear}
                            </span>
                        )}
                        {formData.downPayment > 0 && (
                            <span className="badge bg-warning text-dark">
                                Peşinat: ₺{Number(formData.downPayment).toLocaleString('tr-TR')}
                            </span>
                        )}
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={handlePrev}>
                    Geri Dön
                </Button>
                <Button variant="primary" onClick={handleNext}>
                    Devam Et
                </Button>
            </div>
        </div>
    );
};

export default VehicleForm;